import { memo, useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";

import { useRolesForContext } from "@vega-api/extended/auth/roles/useRolesForContext";
import { type RoleContext } from "@vega-api/generated/types/roleContext";
import { SubjectType } from "@vega-api/generated/types/subjectType";
import { type UserPermissionEntry } from "@vega-api/generated/types/userPermissionEntry";
import { CoreTable } from "@vega-core/components/Table/CoreTable/CoreTable";
import { m } from "@vega-core/paraglide/messages";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { FundCell } from "../../../components/FundCell";
import { RoleSelector } from "../../../components/RoleSelector";
import { groupPermissionsBySubject } from "../../../helpers/groupPermissions";

import { RoleDetails } from "./RoleDetails";

type FundsListProps = {
  permissions: UserPermissionEntry[];
  subjectType: SubjectType;
  roleContext: RoleContext;
  isLoading?: boolean;
  itemsTitle: string;
  className?: string;
};

export const MyFundsList = memo(
  ({
    permissions,
    subjectType,
    roleContext,
    isLoading,
    itemsTitle,
    className,
  }: FundsListProps) => {
    const { getRoleOptionsAsRoles } = useRolesForContext(roleContext);
    const roleOptions = getRoleOptionsAsRoles();

    const roleIdSet = useMemo(
      () => new Set(roleOptions.map((role) => role.id)),
      [roleOptions],
    );

    const manageablePermissions = useMemo(() => {
      const filteredKnownRoles = permissions.filter(
        (item) =>
          item.subjectType === subjectType && roleIdSet.has(item.roleId || ""),
      );

      return groupPermissionsBySubject(filteredKnownRoles);
    }, [permissions, roleIdSet, subjectType]);

    const fundColumn: ColumnDef<
      ReturnType<typeof groupPermissionsBySubject>[0]
    > = {
      id: "fund",
      accessorKey: "name",
      cell: ({ row }) => (
        <FundCell
          assetClass={row.original.assetClass}
          subjectName={row.original.subjectName}
        />
      ),
      header: itemsTitle,
    };

    const roleColumn: ColumnDef<
      ReturnType<typeof groupPermissionsBySubject>[0]
    > = {
      id: "role",
      accessorKey: "selectedRole",
      cell: ({ row }) => {
        const { selectedRole, subjectId, subjectName } = row.original;
        const role = roleOptions.find((r) => r.id === selectedRole);

        return (
          <div className="flex w-full text-right items-center justify-end gap-3">
            {!!role && isTruthy(subjectId) && isTruthy(subjectName) && (
              <RoleDetails
                contextRoles={roleOptions}
                role={role}
                subjectId={subjectId}
                subjectName={subjectName}
              />
            )}

            <RoleSelector
              disabled
              options={roleOptions}
              value={row.original.selectedRole}
            />
          </div>
        );
      },
      header: m.userManagement_permissions_role(),
      align: "right",
    };

    const columns: ColumnDef<
      ReturnType<typeof groupPermissionsBySubject>[0]
    >[] = [fundColumn, roleColumn];

    return (
      <CoreTable
        columns={columns}
        data={manageablePermissions}
        dataTestId="my-funds-list-table"
        emptyState={{
          description:
            subjectType === SubjectType.INVESTMENT
              ? m.userManagement_permissions_noAccessInvestments()
              : m.userManagement_permissions_noAccessOfferings(),
        }}
        isLoading={isLoading}
        rowClassName="border-none bg-background/primary hover:bg-background/tertiary"
        wrapperClassName={className}
      />
    );
  },
);
